import { z } from 'zod'

/**
 * Migration concierge ("Have us move it for you").
 *
 * Scope Scrambl commits to: annual Pro or Business, organization accounts
 * only; one source tool, up to three years of time history, one cutover
 * refresh and one mapping review call. Automations/integrations rebuilds,
 * custom reports and attachments over 5 GB are out of scope.
 *
 * The intake is scope and scheduling only. It NEVER carries credentials —
 * the schema is strict, and free-text answers are screened for pasted tokens.
 */

export const MIGRATION_SOURCE_TOOLS = [
  { id: 'harvest', label: 'Harvest' },
  { id: 'productive', label: 'Productive' },
  { id: 'teamwork', label: 'Teamwork' },
  { id: 'clockify', label: 'Clockify' },
  { id: 'toggl', label: 'Toggl' },
  { id: 'scoro', label: 'Scoro' },
  { id: 'accelo', label: 'Accelo' },
  { id: 'monday', label: 'monday.com' },
  { id: 'other', label: 'Something else' },
] as const

export type MigrationSourceToolId =
  (typeof MIGRATION_SOURCE_TOOLS)[number]['id']

const SOURCE_TOOL_IDS = MIGRATION_SOURCE_TOOLS.map(tool => tool.id) as [
  MigrationSourceToolId,
  ...MigrationSourceToolId[],
]

export const MIGRATION_SCOPE_ITEMS = [
  { id: 'people', label: 'People' },
  { id: 'clients', label: 'Clients' },
  { id: 'contacts', label: 'Contacts' },
  { id: 'projects', label: 'Projects' },
  { id: 'tasks', label: 'Tasks' },
  { id: 'time', label: 'Time entries' },
  { id: 'rates', label: 'Rates' },
  { id: 'budgets', label: 'Budgets' },
  { id: 'invoices', label: 'Invoices' },
  { id: 'expenses', label: 'Expenses' },
] as const

export type MigrationScopeItemId = (typeof MIGRATION_SCOPE_ITEMS)[number]['id']

const SCOPE_IDS = MIGRATION_SCOPE_ITEMS.map(item => item.id) as [
  MigrationScopeItemId,
  ...MigrationScopeItemId[],
]

export const MIGRATION_RATE_MODELS = [
  { id: 'person', label: 'Hourly rate per person' },
  { id: 'role', label: 'Hourly rate per role' },
  { id: 'project', label: 'Rates set per project' },
  { id: 'fixed_fee', label: 'Mostly fixed fee' },
  { id: 'retainer', label: 'Mostly retainers' },
  { id: 'mixed', label: 'A mix' },
  { id: 'not_sure', label: 'Not sure' },
] as const

export type MigrationRateModelId = (typeof MIGRATION_RATE_MODELS)[number]['id']

const RATE_MODEL_IDS = MIGRATION_RATE_MODELS.map(model => model.id) as [
  MigrationRateModelId,
  ...MigrationRateModelId[],
]

/** Tools a self-serve connector is planned for (shown as "Coming soon"). */
export const MIGRATION_CONNECTORS_COMING_SOON: readonly MigrationSourceToolId[] =
  ['harvest', 'productive', 'teamwork', 'clockify', 'scoro']

/** How far back the concierge moves time history. */
export const MIGRATION_MAX_HISTORY_YEARS = 3

export const MIGRATION_REQUEST_STATUSES = [
  'REQUESTED',
  'SCHEDULED',
  'IN_PROGRESS',
  'COMPLETED',
  'CANCELLED',
] as const

export type MigrationRequestStatus = (typeof MIGRATION_REQUEST_STATUSES)[number]

/** Requests that still occupy the organization's one concierge slot. */
export const OPEN_MIGRATION_REQUEST_STATUSES: readonly MigrationRequestStatus[] =
  ['REQUESTED', 'SCHEDULED', 'IN_PROGRESS']

const CALENDAR_DATE = /^\d{4}-\d{2}-\d{2}$/

const calendarDate = z
  .string()
  .regex(CALENDAR_DATE, 'Use a date like 2026-01-31')
  .refine(value => !Number.isNaN(Date.parse(`${value}T00:00:00Z`)), {
    message: 'Not a real date',
  })

/**
 * Free text must not smuggle a credential in. Catches the common shapes
 * ("token: abc…", "api key = …", a long unbroken secret-looking string)
 * without tripping on ordinary prose.
 */
export function looksLikePastedSecret(text: string): boolean {
  if (
    /\b(api[\s_-]?key|access[\s_-]?token|token|password|passwd|secret|bearer|pat)\b\s*[:=]/i.test(
      text,
    )
  ) {
    return true
  }
  return /\b[A-Za-z0-9_\-.]{40,}\b/.test(text)
}

const freeText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .refine(value => !looksLikePastedSecret(value), {
      message:
        'This looks like a password or API token. Never share credentials here — our specialist will set up access with you.',
    })

const personContact = z
  .object({
    name: z.string().trim().min(1, 'Name is required').max(120),
    email: z.string().trim().email('Enter a valid email').max(254),
  })
  .strict()

const count = z.number().int().min(0).max(10_000_000)

export const MigrationRequestIntakeSchema = z
  .object({
    sourceTool: z.enum(SOURCE_TOOL_IDS),
    /** Name of the tool when sourceTool is 'other'. */
    sourceToolOther: z.string().trim().max(120).optional(),
    sourcePlanTier: z.string().trim().max(120).optional(),
    /** Region, data center or account subdomain (acme.harvestapp.com). */
    sourceRegion: z.string().trim().max(255).optional(),
    scope: z
      .array(z.enum(SCOPE_IDS))
      .min(1, 'Pick at least one thing to move')
      .max(SCOPE_IDS.length),
    historyStartDate: calendarDate.optional(),
    includeArchived: z.boolean(),
    approxCounts: z
      .object({
        people: count.optional(),
        clients: count.optional(),
        projects: count.optional(),
        timeEntries: count.optional(),
        invoices: count.optional(),
      })
      .strict()
      .optional(),
    rateModel: z.enum(RATE_MODEL_IDS),
    customFieldsInUse: freeText(2000).optional(),
    invoicesSinceDate: calendarDate.optional(),
    targetCutoverDate: calendarDate.optional(),
    blackoutDays: freeText(500).optional(),
    primaryContact: personContact,
    backupContact: personContact.optional(),
    /** IANA time zone the team works in, for scheduling. */
    timezone: z.string().trim().min(1).max(64),
    /** Temporary admin seat for the Scrambl specialist during the move. */
    consentToSpecialistSeat: z.boolean(),
    notes: freeText(4000).optional(),
  })
  .strict()
  .superRefine((intake, ctx) => {
    if (intake.sourceTool === 'other' && !intake.sourceToolOther?.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['sourceToolOther'],
        message: 'Tell us which tool',
      })
    }
  })

export type MigrationRequestIntake = z.infer<
  typeof MigrationRequestIntakeSchema
>

export const CreateMigrationRequestSchema = z
  .object({ intake: MigrationRequestIntakeSchema })
  .strict()

export type CreateMigrationRequest = z.infer<
  typeof CreateMigrationRequestSchema
>

/**
 * Why the concierge is or is not available. `monthly_plan` and
 * `plan_not_included` get the neutral "Included with annual Pro and Business
 * plans" copy — never an upsell to a personal space (`personal` hides the
 * card entirely).
 */
export type MigrationEligibilityReason =
  | 'eligible'
  | 'monthly_plan'
  | 'plan_not_included'
  | 'no_active_subscription'
  | 'personal'
  | 'feature_disabled'

export interface MigrationEligibility {
  eligible: boolean
  reason: MigrationEligibilityReason
}

export interface MigrationRequestSummary {
  id: string
  sourceTool: string
  status: MigrationRequestStatus
  /** True once the request reached Scrambl's support desk. */
  supportTicketRaised: boolean
  createdAt: string
  updatedAt: string
}

export interface MigrationRequestsResponse {
  eligibility: MigrationEligibility
  requests: MigrationRequestSummary[]
}

export function getMigrationSourceToolLabel(id: string): string {
  return MIGRATION_SOURCE_TOOLS.find(tool => tool.id === id)?.label ?? id
}
